import { gql } from '@apollo/client';
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { PropsWithChildren, useState } from 'react';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import { ExportConfigurationModal } from '~components/configurations/ExportConfigurationModal';
import { UnAssignConfigurationModal } from '~components/configurations/UnAssignConfigurationModal';
import { BulkActionModal_DisplayFragment } from '~components/displays/BulkAction/__generated__/BulkActionModal.graphql';
import { BulkAssignConfigurationInner } from '~components/displays/BulkAction/BulkAssignConfiguration';
import {
  DeviceLogsTarget,
  EnableDeviceLogsModal,
} from '~components/displays/DeviceLogs/EnableDeviceLogsModal';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { ConfigurationSyncStatus } from '~graphql/__generated__/types';
import { DisplayTable_DisplayFragment } from '../__generated__/DisplayTableServerSide.graphql';
import { RowActionsCell_DisplayFragment } from './__generated__/RowActionsCell.graphql';

export interface RowActionsOptions {
  /** Configuration actions; only offered where the Configuration column is (Wave subscription). */
  showConfigurationActions: boolean;
  /** Device log actions; only offered while working in another organization under an access grant. */
  showDeviceLogActions: boolean;
}

/**
 * The display row's "⋮" menu, at the end of the row: every per-display action lives here.
 */
export function RowActionsCell({
  value,
  showConfigurationActions,
  showDeviceLogActions,
}: PropsWithChildren<
  CellProps<
    DisplayTable_DisplayFragment,
    RowActionsCell_DisplayFragment & BulkActionModal_DisplayFragment
  >
> &
  RowActionsOptions) {
  const { verifyUserPermissions } = useAuth();
  const hasConfigurationUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);
  const createConfigurationModal = useDisclosure();
  const unassignConfigurationModal = useDisclosure();
  const assignConfigurationModal = useDisclosure();
  const [deviceLogsTarget, setDeviceLogsTarget] = useState<DeviceLogsTarget | null>(null);
  const isSyncing = value.configurationSyncStatus === ConfigurationSyncStatus.Syncing;

  if (!showConfigurationActions && !showDeviceLogActions) {
    return null;
  }

  return (
    <StopClickPropagation>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          variant="ghost"
          colorScheme="gray"
          size="sm"
          marginLeft="auto"
          display="flex"
          aria-label={t('actions')}
          borderRadius="md"
          _hover={{ bg: 'gray.100' }}
          _active={{ bg: 'gray.100' }}
          icon={
            <Box display="flex" flexDirection="column" alignItems="center" gap="2px">
              <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
              <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
              <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
            </Box>
          }
        />

        {/* Portalled: the sticky cell is its own stacking context, so an inline menu would be
            drawn under the next rows' sticky cells. */}
        <Portal>
          <MenuList p={0.3}>
            {showConfigurationActions && (
              <>
                <MenuItem
                  p={4}
                  fontWeight={'semibold'}
                  textColor={'grey.500'}
                  isDisabled={isSyncing || !hasConfigurationUpdatePermission}
                  onClick={assignConfigurationModal.onOpen}
                >
                  {t('assignNewConfiguration')}
                </MenuItem>
                <MenuItem
                  p={4}
                  fontWeight={'semibold'}
                  textColor={'grey.400'}
                  isDisabled={!value.configuration?.id || !hasConfigurationUpdatePermission}
                  onClick={unassignConfigurationModal.onOpen}
                >
                  {t('unassignConfiguration')}
                </MenuItem>
                <MenuItem
                  p={4}
                  fontWeight={'semibold'}
                  textColor={'grey.300'}
                  isDisabled={isSyncing || !hasConfigurationUpdatePermission}
                  onClick={createConfigurationModal.onOpen}
                >
                  {t('ImportConfiguration')}
                </MenuItem>
              </>
            )}
            {showDeviceLogActions && (
              <MenuItem p={4} fontWeight={'semibold'} onClick={() => setDeviceLogsTarget(value)}>
                {t('enableDeviceLogs')}
              </MenuItem>
            )}
          </MenuList>
        </Portal>
      </Menu>

      {showConfigurationActions && (
        <>
          <ExportConfigurationModal
            displayId={value.id}
            isOpen={createConfigurationModal.isOpen}
            onCancel={createConfigurationModal.onClose}
            defaultName={value.alias + '_config'}
          />
          <UnAssignConfigurationModal
            displayId={value.id}
            isOpen={unassignConfigurationModal.isOpen}
            onCancel={unassignConfigurationModal.onClose}
            configurationId={value.configuration?.id}
          />
          {assignConfigurationModal.isOpen && (
            <Modal
              isOpen={assignConfigurationModal.isOpen}
              onClose={assignConfigurationModal.onClose}
            >
              <ModalOverlay />
              <BulkAssignConfigurationInner
                displays={[value]}
                onCancel={assignConfigurationModal.onClose}
                onBack={assignConfigurationModal.onClose}
                onSuccess={assignConfigurationModal.onClose}
                hideBackButton={true}
              />
            </Modal>
          )}
        </>
      )}
      {showDeviceLogActions && (
        <EnableDeviceLogsModal
          display={deviceLogsTarget}
          onClose={() => setDeviceLogsTarget(null)}
        />
      )}
    </StopClickPropagation>
  );
}

RowActionsCell.graphql = {
  fragments: {
    RowActionsCell_display: gql`
      fragment RowActionsCell_display on Display {
        id
        alias
        serialNumber
        configuration {
          id
        }
        configurationSyncStatus
      }
    `,
  },
};
